import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { GoogleChartInterface, GoogleChartType } from 'ng2-google-charts';
import { WebsiteAuditService } from '../website-audit.service';
import {
  AgentKey,
  AgentOption,
  ExperimentCompetitor,
  ExperimentPage,
  ExperimentResponse,
  ExperimentScoreSeries,
  VisibilityFilter,
} from './agentic-ecommerce.types';
import {
  AGENTS,
  calculateVisibilityScore,
  getAgentMetadata,
  getProducts,
  getScoreColor,
  isProductMissingForPage,
} from './agentic-ecommerce.utils';
import { CoreTargetService } from 'src/app/core/services/target.service';
import { ActivatedRoute } from '@angular/router';
import { DarkmodeService } from 'src/app/services/darkmode.service';

// Match the Overview "score over time" chart palette.
const ACCENT = '#6161ff';
const COMP_COLOR = '#e0a544';

// Distinct line colors for each tracked competitor in the trend chart (matches
// the Overview palette). COMP_COLOR (amber) stays first.
const COMP_PALETTE = [
  '#e0a544',
  '#5b9df0',
  '#c98ae0',
  '#5fcf95',
  '#e26a5d',
  '#4da18c',
  '#dd7bb0',
  '#c8e6a0',
];

@Component({
  selector: 'app-agentic-ecommerce',
  templateUrl: './agentic-ecommerce.component.html',
  styleUrls: ['../audit-theme.css', './agentic-ecommerce.component.css'],
})
export class AgenticEcommerceComponent implements OnInit, OnDestroy {
  constructor(
    private websiteAuditService: WebsiteAuditService,
    private targetService: CoreTargetService,
    private route: ActivatedRoute,
    private darkmodeService: DarkmodeService,
  ) {}

  private subs: Subscription = new Subscription();
  private lastAuditValue: string = '';
  private selectedTask: any = null;
  private target: string = '';
  private lastTarget: string = '';
  // The competitor currently selected in the sub-bar, plus the last key we
  // loaded for, so a competitor switch (same audit) triggers a re-fetch.
  private activeCompetitor: any = null;
  private lastCompetitorKey: string = '';
  // Competitors missing from the (competitor-scoped) experiment payload have
  // their score history fetched separately and cached here — keyed by
  // id/domain — so every tracked competitor draws a line by default.
  // `Fetching` guards in-flight requests so each competitor is fetched once.
  private compTrendCache: {
    [key: string]: { dates: string[]; series: ExperimentScoreSeries };
  } = {};
  private compTrendFetching = new Set<string>();

  public experiment: ExperimentResponse | null = null;
  public isLoading: boolean = false;
  // Subtle page overlay shown while re-fetching for a competitor switch
  // (keeps the current view; no full skeleton).
  public overlayLoading: boolean = false;
  // Sub-bar's own switch phase (getAuditTask) — OR'd in for seamless coverage.
  public competitorSwitching$ = this.websiteAuditService.getCompetitorSwitching();
  public errorMessage: string = '';
  public selectedAgentKey: AgentKey = '';
  public productFilter: VisibilityFilter = 'all';

  public skeletonAgentCards: number[] = [1, 2, 3, 4];
  public skeletonPageCards: number[] = [1, 2];
  public skeletonProducts: number[] = [1, 2, 3, 4, 5, 6];

  private token: string = '';

  // ── Score-over-time trend (same Google LineChart the other tabs use) ──
  public trendHasData = false;
  // Custom clickable dash legend (Google's built-in legend is hidden). Clicking
  // the competitor entry toggles its line; "Your site" stays locked on.
  public trendLegend: {
    key: string;
    label: string;
    color: string;
    isYou: boolean;
    visible: boolean;
  }[] = [];
  private trendVisible: { [key: string]: boolean } = {};
  private isDark = true;
  public trendLineChart: GoogleChartInterface = {
    chartType: GoogleChartType.LineChart,
    dataTable: [],
    options: {
      legend: {
        position: 'top',
        alignment: 'center',
        textStyle: { fontSize: 12, fontName: 'Roboto, sans-serif', color: '#666' },
      },
      pointSize: 4,
      interpolateNulls: true,
      crosshair: { trigger: 'focus', orientation: 'vertical', color: '#eeeeee' },
      focusTarget: 'category',
      tooltip: { textStyle: { fontSize: 12 }, isHtml: true, trigger: 'focus' },
      colors: [ACCENT, COMP_COLOR],
      height: 180,
      backgroundColor: 'transparent',
      chartArea: { left: 40, right: 16, top: 26, bottom: 28, width: '100%', height: '66%' },
      vAxis: {
        minValue: 0,
        maxValue: 100,
        baseline: 0,
        baselineColor: '#c0c0c0',
        viewWindow: { min: 0, max: 100 },
        gridlineColor: '#ededed',
        textStyle: { fontSize: 11, fontName: 'Roboto, sans-serif', color: '#666' },
      },
      hAxis: {
        textStyle: { fontSize: 11, fontName: 'Roboto, sans-serif', color: '#666' },
        gridlines: { color: 'transparent' },
        minorGridlines: { count: 0 },
        baselineColor: 'transparent',
      },
    },
  };

  ngOnInit(): void {
    this.token = this.route.snapshot.paramMap.get('token') as string;
    this.subs.add(
      this.darkmodeService.getDarkMode().subscribe((isDark: boolean) => {
        // Follows the service value (the share shell sets it from the shared-with
        // theme), so the shared page matches the sharer's theme.
        this.isDark = !!isDark;
        this.applyChartTheme();
        this.redrawTrendChart();
      }),
    );
    this.subs.add(
      this.websiteAuditService.getSelectedAuditTask().subscribe((task: any) => {
        this.selectedTask = task;
        this.activeCompetitor = task?.selectedCompetitor || null;
        const audit = this.getAuditValue(task);

        this.target =
          this.targetService.getSelectedTarget('target') ||
          sessionStorage.getItem('target') ||
          '';

        if (!audit) {
          this.resetExperimentState();
          return;
        }

        const competitorKey = this.getCompetitorKey(this.activeCompetitor);
        const sameContext =
          audit === this.lastAuditValue &&
          !!this.experiment &&
          this.target === this.lastTarget;

        if (sameContext) {
          // Same audit — but the sub-bar may have switched competitor. Re-fetch
          // the experiment scoped to the new competitor so cards + chart update.
          if (competitorKey !== this.lastCompetitorKey) {
            this.lastCompetitorKey = competitorKey;
            this.loadExperiment(audit, true);
          } else {
            // Task updated (e.g. scoreHistory arrived on a later payload) —
            // re-plot the trend from the latest task data.
            this.buildTrend();
            this.fetchOtherCompetitorTrends();
          }
          return;
        }

        this.lastTarget = this.target;
        this.lastAuditValue = audit;
        this.lastCompetitorKey = competitorKey;
        // New audit context — drop any cached competitor trends from the prior one.
        this.compTrendCache = {};
        this.compTrendFetching.clear();
        this.loadExperiment(audit);
      }),
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  public selectAgent(agentKey: AgentKey): void {
    const agent = this.agentOptions.find((option) => option.key === agentKey);

    if (!agent || agent.disabled) {
      return;
    }

    this.selectedAgentKey = agentKey;
    // Re-plot the trend for the newly selected agent's score history.
    this.buildTrend();
  }

  public setProductFilter(filter: VisibilityFilter): void {
    this.productFilter = filter;
  }

  public retry(): void {
    const audit = this.getAuditValue(this.selectedTask);

    if (!audit) {
      this.errorMessage = 'No audit is selected yet.';
      return;
    }

    this.loadExperiment(audit);
  }

  public get agentOptions(): AgentOption[] {
    const modelExtractions = this.modelExtractions;
    const competitor = this.competitor;
    const competitorExtractions = this.competitorModelExtractions;
    const competitorTotal = this.competitorTotalProducts;
    const competitorName = this.competitorName;
    const knownAgentKeys = Object.keys(AGENTS);
    const extraAgentKeys = Object.keys(modelExtractions).filter(
      (agentKey) => !knownAgentKeys.includes(agentKey),
    );
    const agentKeys = [...knownAgentKeys, ...extraAgentKeys];

    return agentKeys.map((agentKey) => {
      const metadata = getAgentMetadata(agentKey);
      const disabled = !modelExtractions[agentKey];
      const totalMatchedProducts = Number(
        modelExtractions[agentKey]?.totalMatchedProducts || 0,
      );
      const score = calculateVisibilityScore(
        totalMatchedProducts,
        this.totalProducts,
      );

      return {
        ...metadata,
        key: agentKey,
        score,
        scoreColor: disabled ? '#6f6a65' : getScoreColor(score),
        totalMatchedProducts,
        totalProducts: this.totalProducts,
        disabled,
        competitor: competitor
          ? this.buildCompetitorScore(
              agentKey,
              competitorName,
              competitorExtractions,
              competitorTotal,
            )
          : null,
      };
    });
  }

  private buildCompetitorScore(
    agentKey: AgentKey,
    name: string,
    extractions: Record<AgentKey, { totalMatchedProducts?: number }>,
    total: number,
  ) {
    const disabled = !extractions[agentKey];
    const totalMatchedProducts = Number(
      extractions[agentKey]?.totalMatchedProducts || 0,
    );
    const score = calculateVisibilityScore(totalMatchedProducts, total);

    return {
      name,
      score,
      scoreColor: disabled ? '#6f6a65' : getScoreColor(score),
      totalMatchedProducts,
      totalProducts: total,
      disabled,
    };
  }

  private get experimentCompetitors(): ExperimentCompetitor[] {
    const list = this.experiment?.competitors;
    if (Array.isArray(list) && list.length) {
      return list;
    }
    const single = this.experiment?.competitor;
    return single ? [single] : [];
  }

  // When the payload bundles several competitors, pick the one selected in the
  // sub-bar (by id, then domain); otherwise use the only/first one available.
  public get competitor(): ExperimentCompetitor | null {
    const list = this.experimentCompetitors;
    if (!list.length) {
      return null;
    }
    const active = this.activeCompetitor;
    if (active) {
      if (active.competitorId) {
        const byId = list.find((c) => c.competitorId === active.competitorId);
        if (byId) {
          return byId;
        }
      }
      const activeDomain = this.getDomain(
        active.url || active.domain || active.competitorUrl,
      );
      if (activeDomain) {
        const byDomain = list.find(
          (c) => this.getDomain(c.competitorUrl) === activeDomain,
        );
        if (byDomain) {
          return byDomain;
        }
      }
    }
    return list[0];
  }

  public get competitorName(): string {
    const active = this.activeCompetitor;
    const competitor = this.competitor;
    return (
      active?.name ||
      active?.competitorName ||
      competitor?.competitorName ||
      this.getDomain(
        active?.url ||
          active?.domain ||
          active?.competitorUrl ||
          competitor?.competitorUrl,
      ) ||
      'Competitor'
    );
  }

  private get competitorModelExtractions(): Record<
    AgentKey,
    { totalMatchedProducts?: number }
  > {
    return this.competitor?.result?.modelExtractions || {};
  }

  private get competitorTotalProducts(): number {
    const total = Number(this.competitor?.result?.total || 0);
    return Number.isFinite(total) && total > 0 ? total : 0;
  }

  private getDomain(url?: string): string {
    if (!url) {
      return '';
    }
    try {
      const normalized = url.startsWith('http') ? url : `https://${url}`;
      return new URL(normalized).hostname.replace(/^www\./, '');
    } catch {
      return '';
    }
  }

  public get selectedAgentLabel(): string {
    if (!this.selectedAgentKey) {
      return 'the selected agent';
    }

    return getAgentMetadata(this.selectedAgentKey).label;
  }

  public get pages(): ExperimentPage[] {
    return this.experiment?.htmlExtraction?.pages || [];
  }

  public get visiblePages(): ExperimentPage[] {
    if (!this.selectedAgentKey) {
      return [];
    }

    return this.pages.filter((page) => {
      const products = getProducts(page);

      if (!products.length) {
        return false;
      }

      if (this.productFilter === 'missing') {
        return products.some((product) =>
          isProductMissingForPage(
            product,
            this.selectedAgentKey,
            page.pageType,
          ),
        );
      }

      return true;
    });
  }

  public get hasProducts(): boolean {
    return this.pages.some((page) => getProducts(page).length > 0);
  }

  public get hasAgents(): boolean {
    return this.agentOptions.some((agent) => !agent.disabled);
  }

  public get totalProducts(): number {
    const total = Number(this.experiment?.total || 0);
    return Number.isFinite(total) && total > 0 ? total : 0;
  }

  public get modelExtractions(): Record<
    AgentKey,
    { totalMatchedProducts?: number }
  > {
    return this.experiment?.modelExtractions || {};
  }

  public trackByAgent(index: number, agent: AgentOption): AgentKey {
    return agent.key || String(index);
  }

  public trackByPage(index: number, page: ExperimentPage): string {
    return page.pageUrl || page.pagePath || page.pageTitle || String(index);
  }

  // `silent` keeps the current view in place while re-fetching (used for
  // competitor switches) instead of flashing the full skeleton.
  private loadExperiment(audit: string, silent: boolean = false): void {
    if (silent) {
      this.overlayLoading = true;
    } else {
      this.isLoading = true;
    }
    this.errorMessage = '';

    this.subs.add(
      this.websiteAuditService
        .getCrawlerExperiment(audit, this.token, this.getSelectedCompetitorUrl())
        .subscribe({
          next: (response: ExperimentResponse) => {
            this.experiment = response || {};
            this.isLoading = false;
            this.overlayLoading = false;
            this.syncSelectedAgent();
            this.buildTrend();
            this.fetchOtherCompetitorTrends();
          },
          error: (err: any) => {
            this.isLoading = false;
            this.overlayLoading = false;
            // Don't blank a working view because a competitor re-fetch failed.
            if (!silent) {
              this.experiment = null;
              this.errorMessage =
                err?.error?.message ||
                'We could not load the agentic ecommerce experiment.';
            }
          },
        }),
    );
  }

  private getCompetitorKey(comp: any): string {
    if (!comp) {
      return '';
    }
    return (
      comp.competitorId ||
      this.getDomain(comp.url || comp.domain || comp.competitorUrl) ||
      ''
    );
  }

  private getSelectedCompetitorUrl(): string {
    const comp = this.activeCompetitor;
    return comp?.url || comp?.competitorUrl || '';
  }

  private syncSelectedAgent(): void {
    const agentKeys = this.agentOptions
      .filter((agent) => !agent.disabled)
      .map((agent) => agent.key);

    if (!agentKeys.length) {
      this.selectedAgentKey = '';
      return;
    }

    if (this.selectedAgentKey && agentKeys.includes(this.selectedAgentKey)) {
      return;
    }

    this.selectedAgentKey = agentKeys.includes('openai')
      ? 'openai'
      : agentKeys[0];
  }

  private resetExperimentState(): void {
    this.lastAuditValue = '';
    this.lastCompetitorKey = '';
    this.experiment = null;
    this.errorMessage = '';
    this.selectedAgentKey = '';
    this.productFilter = 'all';
    this.isLoading = false;
    this.overlayLoading = false;
    this.trendHasData = false;
    this.trendLineChart.dataTable = [];
    this.trendLegend = [];
    this.compTrendCache = {};
    this.compTrendFetching.clear();
  }

  // ── Score over time ──────────────────────────────────────────────────
  // Same Google LineChart the Overview/UX/CR tabs use — plot the overall
  // visibility score for the target and (when tracked) the competitor.
  private buildTrend(): void {
    // scoreHistory lives on the audit task (same object the Overview/sub-bar
    // use — its series carry per-agent scores too), with the experiment payload
    // as a fallback in case the backend attaches it there.
    const history =
      this.experiment?.scoreHistory ||
      this.selectedTask?.scoreHistory ||
      this.selectedTask?.overview?.scoreHistory ||
      null;
    const dates: string[] = history?.dates || [];
    const series: ExperimentScoreSeries[] = history?.series || [];

    const targetSeries = series.find((s) => s.type === 'target');

    // Follow the selected AI agent (fall back to the overall score).
    const scoreKey = this.selectedAgentKey || 'overall';
    const targetScores = targetSeries?.scores?.[scoreKey] || [];

    // Show EVERY tracked competitor in the legend by default — not only the ones
    // this (competitor-scoped) experiment payload happens to carry score history
    // for. A competitor with no data still gets a legend entry and plots an empty
    // line (selecting it in the sub-bar re-fetches its own run).
    const trackedComps: any[] = (
      this.selectedTask?.availableCompetitors || []
    ).filter((c: any) => c?.isActive !== false);

    // Match a tracked competitor to its score history — first in this (scoped)
    // payload (by id, then domain), then in the separately-fetched cache. Cached
    // series carry their own dates so we can align them to our timeline by date.
    const seriesForComp = (
      comp: any,
    ): { series: ExperimentScoreSeries | null; dates?: string[] } => {
      const id = comp?.competitorId;
      if (id) {
        const byId = series.find(
          (s) => s.type === 'competitor' && s.competitorId === id,
        );
        if (byId) return { series: byId };
      }
      const dom = this.getDomain(comp?.url || comp?.domain);
      if (dom) {
        const byDom = series.find(
          (s) =>
            s.type === 'competitor' &&
            this.getDomain(s.url || s.domain) === dom,
        );
        if (byDom) return { series: byDom };
      }
      const cached = this.compTrendCache[this.getCompetitorKey(comp)];
      if (cached) return { series: cached.series, dates: cached.dates };
      return { series: null };
    };

    // One entry per competitor, each with a stable color. Prefer the full tracked
    // list; fall back to whatever competitor series exist (e.g. older payloads).
    const comps: {
      series: ExperimentScoreSeries | null;
      dates?: string[];
      key: string;
      label: string;
      color: string;
    }[] = trackedComps.length
      ? trackedComps.map((comp, idx) => {
          const found = seriesForComp(comp);
          return {
            series: found.series,
            dates: found.dates,
            key:
              comp?.competitorId ||
              this.getDomain(comp?.url || comp?.domain) ||
              `comp-${idx}`,
            label:
              comp?.domain ||
              this.getDomain(comp?.url || comp?.domain) ||
              `Competitor ${idx + 1}`,
            color: COMP_PALETTE[idx % COMP_PALETTE.length],
          };
        })
      : series
          .filter((s) => s?.type === 'competitor')
          .map((s, idx) => ({
            series: s,
            dates: undefined,
            key:
              s?.competitorId ||
              this.getDomain(s?.url || s?.domain) ||
              `comp-${idx}`,
            label:
              s?.name ||
              this.getDomain(s?.url || s?.domain) ||
              `Competitor ${idx + 1}`,
            color: COMP_PALETTE[idx % COMP_PALETTE.length],
          }));
    const visibleComps = comps.filter((c) => this.isTrendVisible(c.key));

    // Declare column types explicitly so the domain (column 0) is always
    // numeric — otherwise Google Charts can infer it as string and throws
    // "Data column(s) for axis #0 cannot be of type string".
    const header: any[] = [
      { type: 'number', label: 'Period' },
      { type: 'number', label: 'Your site' },
      { type: 'string', role: 'tooltip', p: { html: true } },
    ];
    visibleComps.forEach((c) => {
      header.push({ type: 'number', label: c.label });
      header.push({ type: 'string', role: 'tooltip', p: { html: true } });
    });

    const rows = dates.map((d, i) => {
      const full = this.fmtDate(d);
      const you = this.numOrNull(targetScores[i]);
      const compVals = visibleComps.map((c) => {
        const arr = c.series?.scores?.[scoreKey] || [];
        if (c.dates) {
          // Cached from a separate scoped fetch — align by date, since that
          // competitor's own run history may not be index-aligned to ours.
          const j = c.dates.indexOf(d);
          return j >= 0 ? this.numOrNull(arr[j]) : null;
        }
        return this.numOrNull(arr[i]);
      });

      let tip =
        `<p class='tooltip-text'><b>${full}</b></p>` +
        `<p class='tooltip-text'><b>Your site: </b>${you !== null ? you : '—'}/100</p>`;
      visibleComps.forEach((c, idx) => {
        const cv = compVals[idx];
        if (cv !== null) {
          tip += `<p class='tooltip-text'><b>${c.label}: </b>${cv}/100</p>`;
        }
      });

      const row: any[] = [i, you, tip];
      visibleComps.forEach((c, idx) => {
        const cv = compVals[idx];
        row.push(cv, cv !== null ? tip : null);
      });
      return row;
    });

    const n = dates.length;
    this.trendLineChart.dataTable = rows.length ? [header, ...rows] : [];
    this.trendLineChart.options['colors'] = [
      ACCENT,
      ...visibleComps.map((c) => c.color),
    ];
    // Hide Google's built-in legend; render our own clickable dash legend.
    this.trendLineChart.options.legend.position = 'none';
    this.trendLegend = [
      { key: 'you', label: 'Your site', color: ACCENT, isYou: true, visible: true },
      ...comps.map((c) => ({
        key: c.key,
        label: c.label,
        color: c.color,
        isYou: false,
        visible: this.isTrendVisible(c.key),
      })),
    ];
    this.trendLineChart.options.hAxis.ticks = dates.map((d, i) => ({
      v: i,
      f: this.fmtShort(d),
    }));
    this.trendLineChart.options.hAxis.viewWindow = {
      min: 0,
      max: n > 1 ? n - 1 : 1,
    };
    this.applyChartTheme();
    // A single data point isn't a trend — need at least two to plot over time.
    this.trendHasData = rows.length > 1;
    // Only (re)draw when there's data — drawing an empty dataTable makes Google
    // Charts default the domain to string and throw the axis #0 error.
    if (this.trendHasData) {
      this.redrawTrendChart();
    }
  }

  // Competitors the (competitor-scoped) experiment payload doesn't include won't
  // draw a line on their own. Fetch each missing competitor's experiment once
  // (same working scoped endpoint the sub-bar uses) purely to harvest its score
  // history, cache it, and re-plot so every tracked competitor gets a line.
  // Failures are ignored (the competitor keeps its legend entry / empty line).
  private fetchOtherCompetitorTrends(): void {
    const audit = this.getAuditValue(this.selectedTask);
    if (!audit) {
      return;
    }
    const history =
      this.experiment?.scoreHistory ||
      this.selectedTask?.scoreHistory ||
      this.selectedTask?.overview?.scoreHistory ||
      null;
    // No trend renders for a single run, so there's nothing to backfill.
    if ((history?.dates || []).length <= 1) {
      return;
    }
    const series: ExperimentScoreSeries[] = history?.series || [];
    const inPayload = (comp: any): boolean => {
      const id = comp?.competitorId;
      if (
        id &&
        series.some((s) => s.type === 'competitor' && s.competitorId === id)
      ) {
        return true;
      }
      const dom = this.getDomain(comp?.url || comp?.domain);
      return (
        !!dom &&
        series.some(
          (s) =>
            s.type === 'competitor' &&
            this.getDomain(s.url || s.domain) === dom,
        )
      );
    };

    const tracked: any[] = (
      this.selectedTask?.availableCompetitors || []
    ).filter((c: any) => c?.isActive !== false);

    tracked.forEach((comp) => {
      const key = this.getCompetitorKey(comp);
      const url = comp?.url || comp?.competitorUrl;
      if (
        !key ||
        !url ||
        inPayload(comp) ||
        this.compTrendCache[key] ||
        this.compTrendFetching.has(key)
      ) {
        return;
      }
      this.compTrendFetching.add(key);
      this.subs.add(
        this.websiteAuditService
          .getCrawlerExperiment(audit, this.token, url)
          .subscribe({
            next: (res: ExperimentResponse) => {
              this.compTrendFetching.delete(key);
              const dates = res?.scoreHistory?.dates || [];
              const compSeries = (res?.scoreHistory?.series || []).filter(
                (s) => s.type === 'competitor',
              );
              const match =
                compSeries.find((s) => this.getCompetitorKey(s) === key) ||
                compSeries[0];
              if (match && dates.length) {
                this.compTrendCache[key] = { dates, series: match };
                this.buildTrend();
              }
            },
            error: () => {
              this.compTrendFetching.delete(key);
            },
          }),
      );
    });
  }

  private isTrendVisible(key: string): boolean {
    return this.trendVisible[key] !== false;
  }
  // Legend click — toggle the competitor line ("Your site" is always shown).
  toggleTrendLine(item: any) {
    if (!item || item.isYou) return;
    this.trendVisible[item.key] = !this.isTrendVisible(item.key);
    this.buildTrend();
  }

  // scoreHistory can carry one competitor entry per available competitor;
  // match the one shown in the cards (by id, then domain), else take the first.
  private pickCompetitorSeries(
    series: ExperimentScoreSeries[],
  ): ExperimentScoreSeries | null {
    const competitors = (series || []).filter((s) => s.type === 'competitor');
    if (!competitors.length) {
      return null;
    }
    const comp = this.competitor;
    if (comp?.competitorId) {
      const byId = competitors.find(
        (s) => s.competitorId === comp.competitorId,
      );
      if (byId) {
        return byId;
      }
    }
    const compDomain = this.getDomain(comp?.competitorUrl);
    if (compDomain) {
      const byDomain = competitors.find(
        (s) => this.getDomain(s.url || s.domain) === compDomain,
      );
      if (byDomain) {
        return byDomain;
      }
    }
    return competitors[0];
  }

  private numOrNull(v: any): number | null {
    // Scores over time are shown as whole numbers (e.g. 55.32 → 55).
    return typeof v === 'number' && !isNaN(v) ? Math.round(v) : null;
  }

  private fmtDate(date: string): string {
    try {
      return new Date(date).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    } catch {
      return date;
    }
  }

  private fmtShort(date: string): string {
    try {
      return new Date(date).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return date;
    }
  }

  private applyChartTheme(): void {
    const o: any = this.trendLineChart.options;
    o.backgroundColor = 'transparent';
    if (this.isDark) {
      o.legend.textStyle.color = '#cfccc6';
      o.vAxis.gridlineColor = '#3b3a3a';
      o.vAxis.baselineColor = '#3b3a3a';
      o.vAxis.textStyle.color = '#cfccc6';
      o.hAxis.textStyle.color = '#cfccc6';
      o.crosshair.color = '#3b3a3a';
    } else {
      o.legend.textStyle.color = '#666';
      o.vAxis.gridlineColor = '#ededed';
      o.vAxis.baselineColor = '#c0c0c0';
      o.vAxis.textStyle.color = '#666';
      o.hAxis.textStyle.color = '#666';
      o.crosshair.color = '#eeeeee';
    }
  }

  private redrawTrendChart(): void {
    setTimeout(() => this.trendLineChart.component?.draw(this.trendLineChart), 0);
  }

  // The crawler experiment is fetched by the audit taskId (Mode A —
  // /crawler/experiment?taskId=…). Fall back to the URL only if a task somehow
  // has no id.
  private getAuditValue(task: any): string {
    return String(task?.taskId || task?.url || '');
  }
}
